import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  GET_VOUCHER_QUERY_KEY,
  getVoucherQueryOptions,
} from '@/features/vouchers/api/get-voucher.api';
import { GET_LIST_VOUCHER_QUERY_KEY } from '@/features/vouchers/api/get-list-voucher.api';
import {
  UpdateVoucherSchema,
  updateVoucherMutationOptions,
} from '@/features/vouchers/api/update-voucher.api';

const TYPE_OPTIONS = [
  { value: 'FLAT', label: 'Flat (Rupiah)' },
  { value: 'PERCENTAGE', label: 'Percentage (%)' },
];

function toDateInputValue(iso: string | undefined) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function toTimeInputValue(iso: string | undefined) {
  if (!iso) return '23:59';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '23:59';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export interface UpdateVoucerDialogProps extends BaseDialogProps {
  voucherId: string;
}

export default function UpdateVoucherDialog({
  dialog,
  voucherId,
}: UpdateVoucerDialogProps) {
  const { closeUpdateVoucherDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const getVoucherQuery = useQuery(
    getVoucherQueryOptions({ voucher_id: voucherId })
  );
  const voucher = getVoucherQuery.data?.result;

  const updateVoucherMutation = useMutation({
    ...updateVoucherMutationOptions(),
    onSuccess: () => {
      toast.success('Voucher berhasil diubah');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_VOUCHER_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_VOUCHER_QUERY_KEY, voucherId],
      });
      closeUpdateVoucherDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengubah voucher';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      voucher_code: voucher?.voucher_code ?? '',
      type: (voucher?.type ?? 'FLAT') as 'FLAT' | 'PERCENTAGE',
      discount: voucher?.discount ?? 0,
      stock: voucher?.stock ?? 0,
      expired_at: toDateInputValue(voucher?.expired_at),
      expired_at_time: toTimeInputValue(voucher?.expired_at),
    },
    onSubmit: async ({ value }) => {
      const { expired_at_time, ...rest } = value;
      const parsed = UpdateVoucherSchema.safeParse({
        ...rest,
        expired_at: new Date(
          `${value.expired_at}T${expired_at_time}:00`
        ).toISOString(),
      });
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Data voucher tidak valid'
        );
        return;
      }
      await updateVoucherMutation.mutateAsync({
        voucher_id: voucherId,
        data: parsed.data,
      });
    },
  });

  const isPending = updateVoucherMutation.isPending;
  const isLoading = getVoucherQuery.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeUpdateVoucherDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ubah Voucher</DialogTitle>
          <DialogDescription>
            Perbarui data voucher di bawah ini.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
            Memuat data voucher...
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field name="voucher_code">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Kode Voucher</Label>
                  <Input
                    id={field.name}
                    placeholder="Masukkan Kode Voucher"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="type">
              {field => (
                <div className="grid gap-2">
                  <Label>Tipe Diskon</Label>
                  <Select
                    className="w-full"
                    placeholder="Pilih Tipe Diskon"
                    options={TYPE_OPTIONS}
                    value={field.state.value}
                    onValueChange={value =>
                      field.handleChange(value as 'FLAT' | 'PERCENTAGE')
                    }
                  />
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field name="discount">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Diskon</Label>
                    <Input
                      id={field.name}
                      type="number"
                      min={0}
                      placeholder="0"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e =>
                        field.handleChange(e.target.valueAsNumber || 0)
                      }
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="stock">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Stok</Label>
                    <Input
                      id={field.name}
                      type="number"
                      min={0}
                      placeholder="0"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e =>
                        field.handleChange(e.target.valueAsNumber || 0)
                      }
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field name="expired_at">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Tanggal Kedaluwarsa</Label>
                    <Input
                      id={field.name}
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="expired_at_time">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Jam Kedaluwarsa</Label>
                    <Input
                      id={field.name}
                      type="time"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeUpdateVoucherDialog}
                disabled={isPending}
              >
                Batal
              </Button>
              <form.Subscribe selector={state => [state.canSubmit]}>
                {([canSubmit]) => (
                  <Button type="submit" disabled={!canSubmit || isPending}>
                    {isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
